// IMPORTAR FUNCIONES DEL PORTAL
import { getCurrentToken } from './Autenticacion';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const API_ENDPOINTS = {
  // Autenticación - AGREGADO verify-session
  auth: {
    login: `${API_BASE_URL}/auth/login`,
    logout: `${API_BASE_URL}/auth/logout`,
    verifySession: `${API_BASE_URL}/auth/verify-session` // NUEVO
  },
  
  representante: `${API_BASE_URL}/representante`,
  asociacion: `${API_BASE_URL}/asociacion`,
  cooperativa: `${API_BASE_URL}/cooperativa`,
  categoria: `${API_BASE_URL}/categoria`,
  puestos: `${API_BASE_URL}/puestos`,
  departamentos: `${API_BASE_URL}/departamentos`,
  municipios: `${API_BASE_URL}/municipios`,
  departamento: `${API_BASE_URL}/departamento`,
  municipio: `${API_BASE_URL}/municipio`,
  detalles_cooperativas: `${API_BASE_URL}/detalles_cooperativas`,
  microsoftProxy: `${API_BASE_URL}/microsoft-proxy/token`,
  baseUrl: API_BASE_URL
};

const apiRequest = async (url, options = {}) => {
  try {
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Aplicacion-Id': process.env.REACT_APP_APLICACION_ID,
    };

    // MODIFICADO: Usar getCurrentToken que maneja portal y local
    const token = getCurrentToken();
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      headers: {
        ...defaultHeaders,
        ...options.headers
      },
      ...options
    };
    const response = await fetch(url, config);
     
    if (!response.ok) {
      const errorText = await response.text();
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (parseError) {
        errorData = { message: errorText };
      }
      
      const apiError = new Error(errorData.error || errorData.message || `Error HTTP: ${response.status}`);
      apiError.status = response.status;
      apiError.response = { data: errorData };
      throw apiError;
    }
    
    const data = await response.json();
    
    return Array.isArray(data) ? data : data.data || data;
  } catch (error) {
    throw error;
  }
};

export const apiService = {
  // ===== AUTENTICACIÓN =====
  auth: {
    login: async (credentials) => {
      try {
        const response = await fetch(API_ENDPOINTS.auth.login, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Aplicacion-Id': process.env.REACT_APP_APLICACION_ID,
          },
          body: JSON.stringify(credentials)
        });

        const responseText = await response.text();
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          throw new Error('Respuesta inválida del servidor');
        }

        if (data.error && data.error.includes('cambiar su contraseña') && data.usuario) {
          return {
            success: true,
            requiresPasswordChange: true,
            usuario: data.usuario,
            message: data.error,
            user: {
              nombre: data.usuario,
              usuario: data.usuario,
              needsPasswordChange: true
            }
          };
        }

        if (response.ok && data.token) {
          return {
            success: true,
            user: data.user || data.empleado,
            token: data.token,
            ...data
          };
        }

        return {
          success: false,
          error: data.error || data.message || 'Usuario o contraseña incorrectos'
        };

      } catch (error) {
        return {
          success: false,
          error: error.message || 'Error de conexión con el servidor'
        };
      }
    },

    logout: async (token) => {
      try {
        return await apiRequest(API_ENDPOINTS.auth.logout, {
          method: 'POST',
          body: JSON.stringify({ token })
        });
      } catch (error) {
        throw new Error("Error al cerrar sesión.");
      }
    }
  },

  // ===== REPRESENTANTES =====
  representantes: {
    getAll: async () => {
      try {
        return await apiRequest(API_ENDPOINTS.representante);
      } catch (error) {
        throw new Error("No se pudieron cargar los representantes.");
      }
    },

    getById: async (id) => {
      try {
        return await apiRequest(`${API_ENDPOINTS.representante}/${id}`);
      } catch (error) {
        throw new Error(`No se pudo cargar el representante con ID ${id}.`);
      }
    },

    create: async (representanteData) => {
      try {
        const result = await apiRequest(API_ENDPOINTS.representante, {
          method: 'POST',
          body: JSON.stringify(representanteData)
        });
        return result;
      } catch (error) {
        if (error.response?.data) {
          const backendError = new Error(error.response.data.error || error.response.data.message || "Error al crear el representante");
          backendError.response = error.response;
          throw backendError;
        }
        
        throw new Error(error.message || "Error al crear el representante.");
      }
    },

    update: async (id, representanteData) => {
      try {
        // MODIFICADO: Usar getCurrentToken
        const token = getCurrentToken();
        if (!token) {
          throw new Error('No hay token de autenticación. Inicie sesión nuevamente.');
        }
        const result = await apiRequest(`${API_ENDPOINTS.representante}/${id}`, {
          method: 'PUT',
          body: JSON.stringify(representanteData)
        });
        
        return result;
        
      } catch (error) {
        throw new Error(error.message || "Error al actualizar el representante.");
      }
    },

    delete: async (id) => {
      try {
        return await apiRequest(`${API_ENDPOINTS.representante}/${id}`, {
          method: 'DELETE'
        });
      } catch (error) {
        throw new Error("Error al eliminar el representante.");
      }
    },

    getByAsociacion: async (codAsociacion) => {
      try {
        const representantes = await apiService.representantes.getAll();
        return representantes.filter(rep => rep.cod_asociacion === codAsociacion);
      } catch (error) {
        throw new Error("Error al obtener representantes de la asociación.");
      }
    },

    getByCategoria: async (categoriaId) => {
      try {
        const representantes = await apiService.representantes.getAll();
        return representantes.filter(rep =>
          rep.categorias && rep.categorias.some(cat =>
            (cat.Id === categoriaId || cat.CategoriaFK === categoriaId) && cat.Estado !== 'Inactivo'
          )
        );
      } catch (error) {
        throw new Error("Error al obtener representantes de la categoría.");
      }
    }
  },

  // ===== MICROSOFT/SHAREPOINT =====
  microsoft: {
    getToken: async (tenantId) => {
      try {
        return await apiRequest(API_ENDPOINTS.microsoftProxy, {
          method: 'POST',
          body: JSON.stringify({ tenantId })
        });
      } catch (error) {
        throw new Error("No se pudo obtener el token de autenticación de Microsoft.");
      }
    },

    uploadFile: async (file, cod_representante, nombre_representante, tenantId = null) => {
      try {
        const tenant = tenantId || process.env.REACT_APP_TENANT_ID;
        
        if (!tenant) {
          throw new Error('tenantId is required');
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('cod_representante', cod_representante);
        formData.append('nombre_representante', nombre_representante);
        formData.append('tenantId', tenant);

        return await apiRequest(`${API_BASE_URL}/microsoft-proxy/upload`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json'
          },
          body: formData
        });
      } catch (error) {
        throw new Error("No se pudo subir el archivo a SharePoint.");
      }
    },

    healthCheck: async (tenantId = null) => {
      try {
        const tenant = tenantId || process.env.REACT_APP_TENANT_ID;
        
        return await apiRequest(`${API_BASE_URL}/microsoft-proxy/health`, {
          method: 'POST',
          body: JSON.stringify({ tenantId: tenant })
        });
      } catch (error) {
        return false;
      }
    },

    getFiles: async (tenantId = null) => {
      try {
        const tenant = tenantId || process.env.REACT_APP_TENANT_ID;
        
        return await apiRequest(`${API_BASE_URL}/microsoft-proxy/files`, {
          method: 'POST',
          body: JSON.stringify({ tenantId: tenant })
        });
      } catch (error) {
        throw new Error("No se pudieron obtener los archivos de SharePoint.");
      }
    }
  },

  // ===== ASOCIACIONES =====
  asociaciones: {
    getAll: async () => {
      try {
        return await apiRequest(API_ENDPOINTS.asociacion);
      } catch (error) {
        throw new Error("No se pudieron cargar las asociaciones.");
      }
    },

    getById: async (id) => {
      try {
        return await apiRequest(`${API_ENDPOINTS.asociacion}/${id}`);
      } catch (error) {
        throw new Error(`No se pudo cargar la asociación con ID ${id}.`);
      }
    }
  },

  // ===== COOPERATIVAS =====
  cooperativas: {
    getAll: async () => {
      try {
        return await apiRequest(API_ENDPOINTS.cooperativa);
      } catch (error) {
        throw new Error("No se pudieron cargar las cooperativas.");
      }
    },

    getById: async (id) => {
      try {
        return await apiRequest(`${API_ENDPOINTS.cooperativa}/${id}`);
      } catch (error) {
        throw new Error(`No se pudo cargar la cooperativa con ID ${id}.`);
      }
    },

    getByAsociacion: async (codAsociacion) => {
      try {
        const cooperativas = await apiService.cooperativas.getAll();
        return cooperativas.filter(coop => coop.cod_asociacion === codAsociacion);
      } catch (error) {
        throw new Error("Error al obtener cooperativas de la asociación.");
      }
    }
  },

  // ===== CATEGORÍAS =====
  categorias: {
    getAll: async () => {
      try {
        return await apiRequest(API_ENDPOINTS.categoria);
      } catch (error) {
        throw new Error("No se pudieron cargar las categorías.");
      }
    },

    getById: async (id) => {
      try {
        return await apiRequest(`${API_ENDPOINTS.categoria}/${id}`);
      } catch (error) {
        throw new Error(`No se pudo cargar la categoría con ID ${id}.`);
      }
    }
  },

  puestos: {
    getAll: async () => {
      try {
        return await apiRequest(API_ENDPOINTS.puestos);
      } catch (error) {
        throw new Error("No se pudieron cargar los puestos.");
      }
    },

    getById: async (id) => {
      try {
        return await apiRequest(`${API_ENDPOINTS.puestos}/${id}`);
      } catch (error) {
        throw new Error(`No se pudo cargar el puesto con ID ${id}.`);
      }
    }
  },

  // ===== GEOGRAFÍA =====
  geografia: {
    getDepartamentos: async () => {
      try {
        return await apiRequest(API_ENDPOINTS.departamentos);
      } catch (error) {
        throw new Error("No se pudieron cargar los departamentos.");
      }
    },

    getDepartamento: async (id) => {
      try {
        return await apiRequest(`${API_ENDPOINTS.departamento}/${id}`);
      } catch (error) {
        throw new Error(`No se pudo cargar el departamento con ID ${id}.`);
      }
    },

    getMunicipios: async () => {
      try {
        return await apiRequest(API_ENDPOINTS.municipios);
      } catch (error) {
        throw new Error("No se pudieron cargar los municipios.");
      }
    },

    getMunicipio: async (id) => {
      try {
        return await apiRequest(`${API_ENDPOINTS.municipio}/${id}`);
      } catch (error) {
        throw new Error(`No se pudo cargar el municipio con ID ${id}.`);
      }
    },

    getMunicipiosByDepartamento: async (codigoDepartamento) => {
      try {
        const municipios = await apiService.geografia.getMunicipios();
        return municipios.filter(mun => mun.CodigoDepartamento === codigoDepartamento);
      } catch (error) {
        throw new Error("Error al obtener municipios del departamento.");
      }
    }
  },

  // ===== DETALLES COOPERATIVAS =====
  detallesCooperativas: {
    create: async (detalleData) => {
      try {
        return await apiRequest(API_ENDPOINTS.detalles_cooperativas, {
          method: 'POST',
          body: JSON.stringify(detalleData)
        });
      } catch (error) {
        throw new Error("Error al crear el detalle de cooperativa.");
      }
    }
  },

// ===== FUNCIONES AUXILIARES ACTUALIZADAS =====
utils: {
  // Para formularios que NO necesitan representantes
  getDatosFormulario: async () => {
    try {
      const [asociaciones, departamentos, municipios, cooperativas] = await Promise.all([
        apiService.asociaciones.getAll(),
        apiService.geografia.getDepartamentos(),
        apiService.geografia.getMunicipios(),
        apiService.cooperativas.getAll(),
      ]);

      return {
        asociaciones,
        departamentos,
        municipios,
        cooperativas,
      };
    } catch (error) {
      throw new Error("No se pudieron cargar los datos auxiliares para el formulario.");
    }
  },

  // Para reportes que SÍ necesitan representantes (requiere login)
  getDatosReporte: async () => {
    try {
      // MODIFICADO: Usar getCurrentToken
      const token = getCurrentToken();
      if (!token) {
        throw new Error('Debe iniciar sesión para acceder a los reportes.');
      }

      const [representantes, asociaciones, departamentos, municipios, cooperativas] = await Promise.all([
        apiService.representantes.getAll(),
        apiService.asociaciones.getAll(),
        apiService.geografia.getDepartamentos(),
        apiService.geografia.getMunicipios(),
        apiService.cooperativas.getAll(),
      ]);

      return {
        representantes,
        asociaciones,
        departamentos,
        municipios,
        cooperativas,
      };
    } catch (error) {
      if (error.status === 401 || error.message.includes('401') || error.message.includes('Token no proporcionado')) {
        configureApi.clearAuthToken();
        throw new Error('Sesión expirada. Por favor, inicie sesión nuevamente.');
      }
      throw new Error("No se pudieron cargar los datos para el reporte.");
    }
  },

  // Para categorías que SÍ necesitan representantes (requiere login)
  getDatosCategorias: async () => {
    try {
      // MODIFICADO: Usar getCurrentToken
      const token = getCurrentToken();
      if (!token) {
        throw new Error('Debe iniciar sesión para acceder a las categorías.');
      }

      const [categorias, representantes, asociaciones, departamentos, puestos] = await Promise.all([
        apiService.categorias.getAll(),
        apiService.representantes.getAll(),
        apiService.asociaciones.getAll(),
        apiService.geografia.getDepartamentos(),
        apiService.puestos.getAll(),
      ]);

      return {
        categorias,
        representantes,
        asociaciones,
        departamentos,
        puestos,
      };
    } catch (error) {
      if (error.status === 401 || error.message.includes('401') || error.message.includes('Token no proporcionado')) {
        configureApi.clearAuthToken();
        throw new Error('Sesión expirada. Por favor, inicie sesión nuevamente.');
      }
      throw new Error("No se pudieron cargar los datos para categorías.");
    }
  },

  // MODIFICADO: Verificar autenticación usando getCurrentToken
  isAuthenticated: () => {
    const token = getCurrentToken();
    return !!token;
  },

  healthCheck: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, { method: 'GET' });
      return response.ok;
    } catch (error) {
      return false;
    }
  }
}};

// Funciones de conveniencia
export const getCategorias = () => apiService.categorias.getAll();
export const getRepresentantes = () => apiService.representantes.getAll();
export const getAsociaciones = () => apiService.asociaciones.getAll();
export const getDepartamentos = () => apiService.geografia.getDepartamentos();
export const getMunicipios = () => apiService.geografia.getMunicipios();
export const getCooperativas = () => apiService.cooperativas.getAll();

export default API_ENDPOINTS;
export { API_BASE_URL, API_ENDPOINTS, apiRequest };

export const handleApiError = (error, defaultMessage = "Error en la operación") => {
  if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
    return "Error de conexión. Verifique su conexión a internet.";
  }
  
  if (error.status) {
    switch (error.status) {
      case 401:
        return "Sesión expirada. Por favor, inicie sesión nuevamente.";
      case 403:
        return "No tiene permisos para realizar esta operación.";
      case 404:
        return "El recurso solicitado no fue encontrado.";
      case 422:
        return "Los datos enviados no son válidos. Verifique la información.";
      case 500:
        return "Error interno del servidor. Intente nuevamente más tarde.";
      default:
        return error.message || defaultMessage;
    }
  }
  
  if (error.message) {
    if (error.message.includes('401')) {
      return "Sesión expirada. Por favor, inicie sesión nuevamente.";
    }
    if (error.message.includes('403')) {
      return "No tiene permisos para realizar esta operación.";
    }
    if (error.message.includes('404')) {
      return "El recurso solicitado no fue encontrado.";
    }
    if (error.message.includes('500')) {
      return "Error interno del servidor. Intente nuevamente más tarde.";
    }
  }
  
  return error.message || defaultMessage;
};

// MODIFICADO: configureApi ahora usa getCurrentToken también
export const configureApi = {
  setAuthToken: (token) => {
    sessionStorage.setItem('authToken', token);
    localStorage.setItem('authToken', token);
  },
  
  clearAuthToken: () => {
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('authToken');
  },
  
  // MODIFICADO: Ahora retorna el token actual (portal o local)
  getAuthToken: () => {
    return getCurrentToken();
  }
};